import {
    type AssignmentResponseType,
    type EstimationRouteType,
    type RealisationStatusType,
    RouteInfoRouteSchema,
    type RouteInfoRouteType,
    type RouteInfoType,
    type RouteInfoTollsType
} from './AssignmentApi';
import type { VehicleResponseType } from '../Vehicles/types';
import { get } from 'svelte/store';
import { planning } from '../../../stores/store';
import { z } from 'zod';
import { QuickCategories } from './QuickCategories/QuickCategories';
import { features } from '../../shared/Features/features';
import { Vehicle } from '../Vehicles/Vehicle';
import {
    AssignmentApi,
    RealisationStatusSchema,
    type ReorderedRouteType,
    RouteInfoSchema,
} from './AssignmentApi';
import { api } from '../../../stores/api';
import { RouteSummaryApp } from './PageDeliveries2/RouteSummaryApp/RouteSummaryApp';
import { DeliveryListApp } from './PageDeliveries2/DeliveryListApp/DeliveryListApp';
import { AssignmentBulkActions } from './AssignmentBulkActions';
import { TimeWindow, type TimeWindowType } from './TimeWindow';
import type { AssignmentJob } from './AssignmentJob';
import { captureMessage } from '../../utilities/Sentry';
import { app } from '../../../stores/app';
import type { TargetStageTypes } from './TargetStage';

export class Assignment {
    private assignment: AssignmentResponseType | undefined;
    private vehicle: VehicleResponseType | undefined;
    private maxDeliveriesPerRoute: number = 200;
    private suggested_jobs: number[];
    private quickCategories: QuickCategories;
    private busy: boolean = false;
    private adhoc_count: number = 0;
    private estimationAlternative: EstimationRouteType | null = null;
    private rsa: RouteSummaryApp = new RouteSummaryApp();
    public deliveryListApp: DeliveryListApp = new DeliveryListApp().setApi(get(api));
    private _nextRoutePoints: number[] = [];
    private _modifiedRouteData: ReorderedRouteType | null = null;
    private _toCompare: Assignment | null = null;
    private _isComparative: boolean = false;

    public toCompare(): Assignment | null {
        return this._toCompare;
    }

    public setToCompare(assignment: Assignment | null): void {
        this._toCompare = assignment;
    }

    constructor(assignment: AssignmentResponseType) {
        this.quickCategories = new QuickCategories();
        this.set(assignment);
        this.suggested_jobs = [];
        if (get(app)?.account?.getSettingsReadonly()?.max_jobs_per_route) {
            this.maxDeliveriesPerRoute = get(app).account.getSettingsReadonly().max_jobs_per_route;
        }
    }

    public setModifiedRouteData(modifiedRouteData: ReorderedRouteType | null) {
        this._modifiedRouteData = modifiedRouteData;
    }

    public getModifiedRouteData(): ReorderedRouteType | null {
        return this._modifiedRouteData;
    }

    public getTimeWindow(aj: AssignmentJob, getFromBaseOnly: boolean = false): TimeWindow | null {
        try {
            // check if getJobId is a function of aj
            if (typeof aj.getJobId !== 'function') {
                console.error('getTimeWindow, aj.getJobId is not a function', aj);
                return null;
            }
            let jobId: number = aj.getJobId();
            let tw: TimeWindowType;
            if (
                this._modifiedRouteData !== null &&
                this._modifiedRouteData.alerts?.time_window[jobId] &&
                !getFromBaseOnly
            ) {
                tw = this._modifiedRouteData.alerts?.time_window[jobId];
            } else {
                tw = this.getRouteInfo()?.alerts?.time_window[jobId || 0];
            }

            // get time window from job if route not exists
            if (!tw || (tw.from === null && tw.to === null)) {
                tw = {
                    from: aj.getJob()?.special_attributes?.start_of_time_window || null,
                    to: aj.getJob()?.special_attributes?.end_of_time_window || null,
                    eta: null,
                    fulfilled: null,
                    delivered: null,
                };
            }

            if (tw) {
                // console.log('getTimeWindow', tw);
                return new TimeWindow(tw);
            } else {
                return null;
            }
        } catch (e) {
            console.error('getTimeWindow error', e, aj);
            captureMessage('getTimeWindow error', {
                extra: {
                    aj: aj,
                    e: e,
                },
            });
            return null;
        }
    }

    public async saveNextRoutePoints(nextRoutePoints: number[]) {
        if (JSON.stringify(this._nextRoutePoints) !== JSON.stringify(nextRoutePoints)) {
            console.log(
                'getRealisationStatus, getNextRoutePointJobId',
                this._nextRoutePoints,
                nextRoutePoints,
                this.getId()
            );
        } else {
            console.log(
                'getRealisationStatus, getNextRoutePointJobId, no change',
                nextRoutePoints,
                this.getId()
            );
        }
        this._nextRoutePoints = nextRoutePoints;
    }

    async reload(): Promise<Assignment> {
        let r;
        if (this.getId() > 0) {
            r = await AssignmentApi.getOne(get(api), this.getId());
        } else {
            r = await AssignmentApi.getOneWithCounters(get(api), get(planning).selectedDay);
        }
        if (r) {
            this.set(r);
        } else {
            // console.error('Failed to reload assignment');
            throw new Error('Failed to reload assignment');
        }
        return this;
    }

    public setScenarioId(scenarioId: number | null): void {
        this.assignment.scenario_id = scenarioId;
    }

    public setRouteInfoInProgress(): this {
        this.setRouteInfo({
            ...this.getRouteInfo(),
            stage: 'routing',
            route: { inprogress: true, _setAt: new Date().getTime() },
        });
        return this;
    }

    public isRouteInvalid(): boolean {
        return !!this.assignment.route_info?.route?.invalid;
    }

    async prepareRouteForRecalculation() {
        let routeInfo: RouteInfoType = JSON.parse(JSON.stringify(this.getRouteInfo()));
        if (routeInfo && routeInfo.stage) {
            delete routeInfo.stage;
            if (routeInfo.progress) {
                delete routeInfo.progress;
            }
            await AssignmentApi.setRouteInfo(get(api), this.getId(), routeInfo);
        }
    }

    public setAdhocCount(count: number): void {
        this.adhoc_count = count;
    }

    public getAdhocCount(): number {
        return this.adhoc_count;
    }

    public set(assignment: AssignmentResponseType): Assignment {
        let routeInfo = this.getRouteInfo();
        if (
            routeInfo?.route?.inprogress &&
            routeInfo?.route?._setAt &&
            !assignment.route_info?.stage &&
            routeInfo?.route?._setAt > new Date().getTime() - 5000
        ) {
            //when inprogress flag has beet set in frontend, do not update it with data from server, which could be out of date
            assignment.route_info = routeInfo;
        }
        this.assignment = assignment;
        this.estimationAlternative = null;
        this.rsa.importBaseRouteFromAssignment(this);
        this.rsa.importMaximumRouteFromAssignment(this);
        this.checkRouteInfo(this.assignment.route_info);
        return this;
    }

    public getRsa(): RouteSummaryApp {
        return this.rsa;
    }

    public get(): AssignmentResponseType {
        return this.assignment;
    }

    public isDefault(): boolean {
        try {
            return this.assignment?.type === 'default' || false;
        } catch (e) {
            console.trace(e); // Issue # LASTMILE-2-H3
            console.error(e);
            return false;
        }
    }

    public isVehicleGroup(): boolean {
        return this.getType() === 'group-parent';
    }

    public isAdHoc(): boolean {
        return this.assignment?.type === 'adhoc' || false;
    }

    public getId(): number {
        return this.assignment.id;
    }

    // public getJobId(): number {
    //     return this.assignment.job_id;
    // }

    getName(): string {
        return this.assignment.name;
    }

    public getStartDateRoute(): string {
        return this.assignment.start_time_route;
    }

    public setStartDateRoute(start_time_route: string): void {
        try {
            z.string()
                .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}$/)
                .parse(start_time_route);
            this.assignment.start_time_route = start_time_route;
        } catch (e) {
            console.error(e);
        }
    }

    public getStartHourRouteInSeconds(): number {
        let eta_hour = this.getStartHourRoute();
        const [hour, minute] = eta_hour.split(':').map(Number);
        let eta_seconds = hour * 3600 + minute * 60;
        return eta_seconds;
    }

    public getEndHourRouteInSeconds(): number {
        let dur = this.getRouteDuration('s');
        if (dur === null) {
            return this.getStartHourRouteInSeconds();
        }
        return (this.getStartHourRouteInSeconds() + dur) % (24 * 3600);
    }

    public getStartHourRoute(): string {
        return this.assignment.start_time_route.split(' ')[1].substring(0, 5);
    }

    public setVehicleFromAll(vehicles: VehicleResponseType[]): Assignment {
        this.vehicle = vehicles.find(vehicle => {
            return vehicle.id === this.getVehicleId();
        });
        this.assignment.vehicle_id = this.vehicle?.id || null;
        return this;
    }

    public setVehicleFromAll2(vehicles: Vehicle[]): Assignment {
        this.vehicle =
            vehicles
                .find(vehicle => {
                    return vehicle.getId() === this.getVehicleId();
                })
                ?.get() || undefined;
        this.assignment.vehicle_id = this.vehicle?.id || null;
        return this;
    }

    public setVehicle(vehicle: VehicleResponseType): Assignment {
        this.vehicle = vehicle;
        this.assignment.vehicle_id = vehicle.id;
        return this;
    }

    // public setVehicleId(vehicleId: number | null): void {
    //     this.assignment.vehicle_id = vehicleId;
    //     this.vehicle = undefined;
    // }
    //
    // public setVehicleUnset(): void {
    //     this.assignment.vehicle_id = null;
    //     this.vehicle = { ...this.vehicle, id: null };
    // }

    public getVehicleId(): number | null {
        return this.assignment.vehicle_id;
    }

    public getVehicle(): VehicleResponseType | undefined {
        return this.vehicle;
    }

    public getVehicleName(): string {
        if (this.vehicle) {
            return this.vehicle.name || '';
        }
        return ''; // this.assignment.vehicle_id && this.assignment.vehicle_id.toString() || ''; // FIXME: add real vehicle name
    }

    public getZoneName(): string {
        return (this.assignment?.zone_id && this.assignment?.name) || '';
    }

    // public getZoneId(): number | null {
    //     return this.assignment?.zone_id || null;
    // }

    public getRouteDuration(unit: string = 's', base: boolean = false): number | null {
        if (!base && this._modifiedRouteData) {
            return this._modifiedRouteData.eta_with_stops_m !== null
                ? this._modifiedRouteData.eta_with_stops_m * 60
                : null;
        }
        if (!this.isRouteCalculated()) return null;
        // return this.assignment?.route_info?.route?.eta_with_stops_m && this.assignment?.route_info?.route?.eta_with_stops_m*60 || null;
        return this.assignment?.route_info &&
            this.assignment?.route_info?.route &&
            this.assignment?.route_info?.route?.eta_with_stops_m !== null
            ? this.assignment.route_info.route.eta_with_stops_m * 60
            : null;
    }

    public getRouteDistance(unit: string = 'm'): number | null {
        if (!this.isRouteCalculated()) return null;
        return this.assignment?.route_info &&
            this.assignment?.route_info?.route &&
            this.assignment?.route_info?.route?.distance_m !== null
            ? this.assignment.route_info.route.distance_m
            : null;
    }

    public getRouteTolls(): RouteInfoTollsType | null {
        if (!this.isRouteCalculated()) return null;
        return this.assignment?.route_info &&
            this.assignment?.route_info?.route &&
            this.assignment?.route_info?.route?.tolls
            ? this.assignment.route_info.route.tolls
            : null;
    }

    public getRouteCalculationProgressAsNumber(): number {
        return this.assignment?.route_info?.progress?.percent || 0;
    }

    // public getRouteCalculationProgressAsString(): string {
    //     if (
    //         this.assignment?.route_info?.route?.inprogress &&
    //         this.assignment?.route_info?.progress
    //     ) {
    //         return (this.assignment?.route_info?.progress.percent || 0) + '%';
    //     } else {
    //         return '';
    //     }
    // }

    public getRouteCalculationProgress(): any {
        if (this.assignment?.route_info?.route?.inprogress) {
            return this.assignment?.route_info?.progress;
        } else {
            return null;
        }
    }

    public hasVehicleAssigned(): boolean {
        return this.assignment.vehicle_id !== null;
    }

    // public hasZoneAssigned(): boolean {
    //     return this.assignment.name !== null;
    // }

    public getCountDeliveries(): number {
        let count = 0;
        count = this.assignment?.details?.jobs || 0;
        return Math.max(count, 0);
    }

    // public increaseCountDeliveries(): void {
    //     if (this.assignment.jobs) {
    //         this.assignment.jobs++;
    //     }
    //     if (this.assignment?.assoc_count) {
    //         this.assignment.assoc_count.assignment_job++;
    //     } else {
    //         this.assignment.assoc_count = { assignment_job: 1 };
    //     }
    //     if (typeof this.assignment.details?.jobs !== 'undefined') {
    //         this.assignment.details.jobs = this.assignment.details.jobs + 1;
    //     }
    //     console.log('increaseCountDeliveries', this.assignment.assoc_count.assignment_job);
    //     this.updateQuickCategories();
    // }

    // public decreaseCountDeliveries(): void {
    //     if (this.assignment.jobs) {
    //         this.assignment.jobs--;
    //     }
    //     if (this.assignment?.assoc_count) {
    //         this.assignment.assoc_count.assignment_job--;
    //     } else {
    //         this.assignment.assoc_count = { assignment_job: 0 };
    //     }
    //     if (typeof this.assignment.details?.jobs !== 'undefined') {
    //         this.assignment.details.jobs = this.assignment.details.jobs - 1;
    //     }
    //     console.log(
    //         'decreaseCountDeliveries',
    //         this.assignment.jobs,
    //         this.assignment.assoc_count.assignment_job
    //     );
    //     this.updateQuickCategories();
    // }

    public getCountParcels(): number {
        return this.assignment?.details?.parcels || 0;
    }

    public getCountParcelsToDeliver(): number {
        return this.assignment?.details?.parcels_to_deliver || 0;
    }

    public getCountParcelsToCollect(): number {
        return this.assignment?.details?.parcels_to_collect || 0;
    }

    public getTotalWeight(): number {
        return this.assignment?.details?.total_weight || 0;
    }

    public getTotalWeightToDeliver(): number {
        return this.assignment?.details?.total_weight_to_deliver || 0;
    }

    public getTotalWeightToCollect(): number {
        return this.assignment?.details?.total_weight_to_collect || 0;
    }

    private _get() {
        return this.assignment;
    }

    public isRouteCalculated(): boolean {
        return (
            this.assignment.route_info?.stage === 'completed' &&
            !!this.assignment.route_info?.route?.id
        );
    }

    public isRouteManuallyModified(): boolean {
        return this.assignment.route_info?.route?.modified === true || false;
    }

    public isRouteTracable(): boolean {
        return (
            this.assignment.route_info?.route?.optimized ||
            this.assignment.route_info?.route?.incremental ||
            this.assignment.route_info?.route?.ordered ||
            false
        );
    }

    public getRouteInfo(): RouteInfoType {
        return this.assignment?.route_info ?? null;
    }

    public setRouteInfo(routeInfo: RouteInfoType): void {
        // console.log('setRouteInfo', routeInfo);
        this.checkRouteInfo(routeInfo);
        this.assignment = { ...this.assignment, route_info: routeInfo };
    }

    public checkRouteInfo(routeInfo: RouteInfoType): void {
        if (typeof routeInfo === 'undefined') {
            console.info('checkRouteInfo, routeInfo is undefined');
            // captureMessage('RouteInfoSchema.parse routeInfo is undefined');
            return;
        }
        try {
            RouteInfoSchema.parse(routeInfo);
        } catch (err) {
            console.error(err);
            captureMessage('RouteInfoSchema.parse', {
                extra: {
                    err: err,
                    routeInfo: routeInfo,
                },
            });
        }
    }

    // public removeRouteInfo(): void {
    //     this.assignment = { ...this.assignment, route_info: null };
    // }

    public isRouteInCalculation(): boolean {
        // return this.assignment.route_info?.stage && this.assignment.route_info.stage !== 'completed' || false;
        return this.assignment.route_info?.route?.inprogress || false;
    }

    // public setRouteInCalculation() {
    //     if (this.assignment.route_info?.route) {
    //         this.assignment.route_info.route.inprogress = true;
    //         this.assignment.route_info.progress = { percent: 0 };
    //     }
    // }

    public isRouteCalculationFailed(): boolean {
        return this.assignment.route_info?.route?.error || false;
    }

    // public setRouteCalculationStatus(status: 'completed'): void {
    //     this.assignment.route_info = { ...this.assignment.route_info, stage: status };
    // }

    public getRouteId(): string | null {
        return this.assignment.route_info?.route?.id || null;
    }

    public getType(): string {
        return this.assignment.type;
    }

    public setLocked(locked: boolean): this {
        this.assignment.committed = locked;
        this.updateQuickCategories();
        return this;
    }

    public isLocked(): boolean {
        return this.assignment.committed;
    }

    // public changeAssignmentId(newId: number): void {
    //     this.assignment.id = newId;
    // }

    public getScenarioId(): number | null {
        return this.assignment.scenario_id;
    }

    public getPinOpts(suggestion: boolean = false): any {
        if (this.assignment?.id) {
            let opts: any = { backgroundColor: '#ffffff' };
            if (suggestion) {
                opts.colorOpacity = 0.5;
            }
            return opts;
        } else {
            return { backgroundColor: '#d93b4d', borderColor: '#ff0000' };
        }
    }

    public canRouteBeCalculated(): boolean {
        return (
            this.isDefault() &&
            this.hasVehicleAssigned() &&
            !this.isRouteInCalculation() &&
            this.getCountDeliveries() > 0 &&
            this.getCountDeliveries() <= this.maxDeliveriesPerRoute
        );
    }

    public canRouteBeCalculatedWithAccept(): boolean {
        return (
            this.isDefault() &&
            this.hasVehicleAssigned() &&
            !this.isRouteInCalculation() &&
            (this.getCountDeliveries() > 0 || this.getCountSuggestedJobs() > 0) &&
            this.getCountDeliveries() + this.getCountSuggestedJobs() <= this.maxDeliveriesPerRoute
        );
    }

    public getMaxDeliveriesPerRoute(): number {
        return this.maxDeliveriesPerRoute;
    }

    public isDownloaded(): boolean {
        // is route downloaded by driver (but only for first time)
        try {
            z.string()
                .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}$/)
                .parse(this.assignment.route_info?.route?.downloaded);
            return true;
        } catch (e) {
            return false;
        }
    }

    public isRealised(): boolean {
        // is route completed by driver
        try {
            let completed =
                this.assignment.route_info?.current?.completed ??
                this.assignment.route_info?.route?.completed;
            z.string()
                .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}$/)
                .parse(completed);
            return true;
        } catch (e) {
            return false;
        }
    }

    public getCountSuggestedJobs(): number {
        return this.assignment?.details?.suggestions || 0;
    }

    // public getSuggestedJobs(): number[] {
    //     return this.suggested_jobs;
    // }

    public addSuggestedJob(jobId: number): void {
        this.suggested_jobs.push(jobId);
        this.updateQuickCategories();
    }

    public setQuickCategories(quickCategories: QuickCategories): void {
        this.quickCategories = quickCategories;
        this.updateQuickCategories();
    }

    public getQuickCategories(): QuickCategories {
        return this.quickCategories;
    }

    public getCountRealisations(): number {
        return this.assignment?.details?.realisations || 0;
    }

    public updateQuickCategories(): void {
        this.quickCategories.updateQuantity(
            'to_assign',
            this.isAdHoc()
                ? this.assignment?.details?.unset || 0
                : this.isDefault() || this.isVehicleGroup()
                  ? 0
                  : this.getCountDeliveries()
        );
        this.quickCategories.updateQuantity(
            'to_approve',
            this.isDefault() || this.isAdHoc() || this.isVehicleGroup()
                ? this.getCountSuggestedJobs()
                : 0
        );
        this.quickCategories.updateQuantity(
            'to_send',
            this.isDefault() || this.isAdHoc() || this.isVehicleGroup()
                ? this.getCountDeliveries()
                : 0
        );
        this.quickCategories.updateQuantity(
            'in_progress',
            this.isDefault() || this.isAdHoc() || this.isVehicleGroup()
                ? this.getCountRealisations()
                : 0
        );
        // this.quickCategories.updateAdhoc('to_assign', this.assignment?.details?.unset || 0);
        // this.quickCategories.updateAdhoc('to_approve', 0);
        // this.quickCategories.updateAdhoc('to_send', 0);
        // this.quickCategories.updateAdhoc('in_progress', 0);
    }

    public hasEstimation(): boolean {
        return (
            typeof this.assignment?.route_info?.estimate !== 'undefined' &&
            this.assignment?.route_info?.estimate != null
        );
    }

    public getEstimation(): EstimationRouteType | null {
        if (this.hasEstimation()) {
            return this.assignment?.route_info?.estimate;
        }
        return null;
    }

    public getRouteEstimatedDistance(unit: string = 'm'): number | null {
        if (this.hasEstimation()) {
            return this.assignment?.route_info &&
                this.assignment?.route_info?.estimate &&
                this.assignment?.route_info?.estimate?.distance_m !== null
                ? this.assignment.route_info.estimate.distance_m
                : null;
        }
        return null;
    }

    public getRouteEstimatedDuration(unit: string = 's'): number | null {
        // console.log('!!! getRouteEstimatedDuration', this.hasEstimation());
        if (this.hasEstimation()) {
            // console.log('!!! getRouteEstimatedDuration', this.assignment?.route_info?.estimate);
            return this.assignment?.route_info &&
                this.assignment?.route_info?.estimate &&
                this.assignment?.route_info?.estimate?.eta_with_stops_m !== null
                ? this.assignment.route_info.estimate.eta_with_stops_m * 60
                : null;
        }
        return null;
    }

    public getRouteEstimatedTolls(): RouteInfoTollsType | null {
        if (this.hasEstimation()) {
            return this.assignment?.route_info &&
                this.assignment?.route_info?.estimate &&
                this.assignment?.route_info?.estimate?.tolls
                ? this.assignment.route_info.estimate.tolls
                : null;
        }
    }

    public getRouteEstimatedKey(): string | null {
        return this.assignment?.route_info?.estimate?.estimate_key || null;
    }

    public setEstimation(estimate: EstimationRouteType | null) {
        if (!this.assignment.route_info) {
            this.assignment.route_info = { stage: null, estimate: null };
        }
        if (this.assignment.route_info) {
            if (estimate !== null) {
                this.assignment.route_info.estimate = estimate;
            } else {
                delete this.assignment.route_info.estimate;
            }
            this.rsa.importBaseRouteFromAssignment(this);
        }
    }

    public setEstimationAlternative(estimate: EstimationRouteType) {
        this.estimationAlternative = estimate;
    }

    public getEstimationAlternative(): EstimationRouteType | null {
        return this.estimationAlternative;
    }

    public updateRealisationStatus(status: RealisationStatusType) {
        status = RealisationStatusSchema.parse(status);
        if (typeof status === 'undefined') {
            console.warn('updateRealisationStatus, status is undefined');
            return;
        }
        if (this.assignment && this.assignment.route_info) {
            if (status.base_route) {
                this.assignment.route_info.route = status.base_route;
            } else {
                if (status.downloaded && this.assignment.route_info.route) {
                    this.assignment.route_info.route.downloaded = status.downloaded;
                }
                if (status.completed && this.assignment.route_info.route) {
                    this.assignment.route_info.route.completed = status.completed;
                }
                if (status.invalid && this.assignment.route_info.route) {
                    this.assignment.route_info.route.invalid = status.invalid;
                }
            }
            if (status.current_route) {
                this.assignment.route_info.current = status.current_route;
            } else if (this.assignment.route_info.current) {
                delete this.assignment.route_info.current;
            }
            if (status.alerts && !this.assignment.route_info.current) {
                this.assignment.route_info.alerts = status.alerts;
            }
            if (status.details) {
                this.assignment.details = status.details;
                this.updateQuickCategories();
            }
            this.assignment.route_info.realisation_status = status;
        } else if (this.isAdHoc() && status.details) {
            this.assignment.details = status.details;
            this.updateQuickCategories();
        }
    }

    public getRealisationStatus(): RealisationStatusType | null {
        return this.assignment?.route_info?.realisation_status ?? null;
    }

    public isRouteModifiedAfterDownload(): boolean {
        // route is modified after download by driver, and is waiting for approval by driver
        // route_info.current exists only when there is a modification (difference between downloaded route by driver and current route defined by operator)
        return typeof this.assignment?.route_info?.current != 'undefined';
    }

    public isRouteModifiedByDriver(): boolean {
        // ???
        return this.assignment?.route_info?.route?.modifiedBy?.role === 'driver';
    }

    public hasRouteBeenEverDownloaded(): boolean {
        return this.isDownloaded() || this.isRouteModifiedAfterDownload();
    }

    public isRouteOrderModified(): boolean {
        console.log(
            'modified_by_operator ',
            this.assignment?.route_info?.reorder_stack?.length,
            this.assignment?.route_info?.reorder_stack
        );
        return (
            (this.assignment?.route_info?.reorder_stack &&
                this.assignment?.route_info.reorder_stack.length > 0) ??
            false
        );
    }

    public getRouteModifiedByOperator(): RouteInfoRouteType | undefined {
        if (this.assignment?.route_info?.reorder_stack && this.assignment.route_info.reorder_stack.length > 0) {
            return this.assignment.route_info.reorder_stack[this.assignment.route_info.reorder_stack.length-1];
        }
        return undefined;
    }

    public getRouteInRealisation(): RouteInfoRouteType | undefined {
        return this.assignment?.route_info?.current;
    }

    public setColor(color: string | null): void {
        this.assignment.color = color;
    }

    public getColor(): string {
        return this.assignment?.color || (this.assignment?.id ? '#ffffff' : '#f9e196');
    }

    public setBusy(busy: boolean): this {
        this.busy = busy;
        return this;
    }

    public isBusy(): boolean {
        return this.busy;
    }

    public setTargetStage(targetStage: TargetStageTypes) {
        if (this.assignment) {
            this.assignment.stage_target = targetStage;
            this.assignment.stage_target_processed = false;
        }
    }

    public getVehicleWorkTimeLimitSeconds(vehicle: VehicleResponseType): number | null {
        if (typeof this.assignment === 'undefined') {
            console.warn('work_time - assignment is undefined');
            return null;
        }
        let v = new Vehicle().set(vehicle);
        return v.getWorkTimeForDay(this.assignment.start_time_route);
    }

    public getVehicleWorkTimeStartSeconds(vehicle: VehicleResponseType): number | null {
        if (typeof this.assignment === 'undefined') {
            console.warn('work_time - assignment is undefined');
            return null;
        }
        let v = new Vehicle().set(vehicle);
        return v.getWorkTimeStartForDay(this.assignment.start_time_route);
    }

    public getVehicleWorkTimeEndSeconds(vehicle: VehicleResponseType): number | null {
        if (typeof this.assignment === 'undefined') {
            console.warn('work_time - assignment is undefined');
            return null;
        }
        let v = new Vehicle().set(vehicle);
        return v.getWorkTimeEndForDay(this.assignment.start_time_route);
    }

    public isBeingProcessed(): boolean {
        return !this.assignment?.stage_target_processed;
    }

    public isComparative(): boolean {
        return this._isComparative;
    }

    public setComparative(comparative: boolean) {
        this._isComparative = comparative;
    }
}
